import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { timeAgo } from '@/lib/time';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LayoutGrid, Settings2, LogOut, User, ChevronDown, KeyRound, Eye, Users, Palette, Bell, Check, X, UserCheck } from 'lucide-react';

export function TopBar() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const canManage = user?.permission === 'manage';
  const brandName = settings?.brand_name || 'Commissioning Tools';
  const domain = settings?.domain || 'cxatools.com';
  const logo = settings?.logo;

  const { data: team = [] } = useQuery({
    queryKey: ['team'],
    queryFn: async () => (await api.get('/team')).data,
    enabled: canManage,
    refetchInterval: 60000,
  });
  const pending = team.filter((u) => u.status === 'pending');
  const pendingCount = pending.length;
  const [busyId, setBusyId] = React.useState(null);

  const refreshTeam = () => qc.invalidateQueries({ queryKey: ['team'] });
  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/team/${id}/approve`, { permission: 'view' });
      toast.success('Access approved', { description: 'Added as View only — refine access in Team.' });
      refreshTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    } finally { setBusyId(null); }
  };
  const reject = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/team/${id}`);
      toast.success('Request rejected');
      refreshTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject');
    } finally { setBusyId(null); }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const isAdmin = location.pathname.startsWith('/admin');
  const isTeam = location.pathname.startsWith('/settings') && location.search.includes('tab=team');
  const initials = (user?.name || user?.email || 'CX').slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--cx-border)] bg-[rgba(7,10,15,0.7)] backdrop-blur-xl">
      <div className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="topbar-brand">
          {logo ? (
            <img src={logo} alt={brandName} className="h-8 w-8 rounded-lg object-cover border border-[color:var(--cx-border)]" />
          ) : (
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--cx-brand-gradient)' }}>
              <LayoutGrid className="h-4 w-4 text-[#04120f]" />
            </div>
          )}
          <div className="leading-tight">
            <div className="font-display text-[15px] tracking-tight text-[color:var(--cx-fg)]">{brandName}</div>
            <div className="font-mono text-[10px] text-[color:var(--cx-muted)] -mt-0.5">{domain}</div>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <Button asChild variant={!isAdmin && !isTeam ? 'secondary' : 'ghost'} size="sm" className="hidden sm:inline-flex">
            <Link to="/" data-testid="topbar-dashboard-link"><LayoutGrid className="h-4 w-4 mr-1.5" />Dashboard</Link>
          </Button>
          {canManage && (
            <Button asChild variant={isAdmin ? 'secondary' : 'ghost'} size="sm" className="hidden sm:inline-flex">
              <Link to="/admin" data-testid="topbar-admin-link"><Settings2 className="h-4 w-4 mr-1.5" />Apps</Link>
            </Button>
          )}
          {canManage && (
            <Button asChild variant={isTeam ? 'secondary' : 'ghost'} size="sm" className="hidden sm:inline-flex relative">
              <Link to="/settings?tab=team" data-testid="topbar-team-link">
                <Users className="h-4 w-4 mr-1.5" />Team
                {pendingCount > 0 && (
                  <Badge data-testid="topbar-team-pending-badge"
                    className="ml-1.5 h-5 min-w-5 px-1.5 justify-center rounded-full text-[10px]"
                    style={{ background: 'var(--cx-primary)', color: '#04120f', border: 'none' }}>
                    {pendingCount}
                  </Badge>
                )}
              </Link>
            </Button>
          )}

          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="topbar-notifications-button" aria-label="Notifications"
                  className="relative h-9 w-9 rounded-full border border-[color:var(--cx-border)] bg-[color:var(--cx-surface)] flex items-center justify-center text-[color:var(--cx-fg-muted)] hover:text-[color:var(--cx-fg)] hover:border-[rgba(46,230,197,0.4)] transition-colors">
                  <Bell className="h-4 w-4" />
                  {pendingCount > 0 && (
                    <span data-testid="topbar-notifications-badge"
                      className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center"
                      style={{ background: 'var(--cx-primary)', color: '#04120f' }}>
                      {pendingCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0">
                <div className="px-3 py-2.5 border-b border-[color:var(--cx-border)] flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-[color:var(--cx-primary)]" />
                  <span className="text-sm font-medium text-[color:var(--cx-fg)]">Access requests</span>
                  {pendingCount > 0 && (
                    <Badge className="ml-auto h-5 min-w-5 px-1.5 justify-center rounded-full text-[10px]"
                      style={{ background: 'rgba(46,230,197,0.14)', color: 'var(--cx-primary)', border: '1px solid var(--cx-border)' }}>
                      {pendingCount}
                    </Badge>
                  )}
                </div>
                <div className="max-h-80 overflow-auto" data-testid="topbar-notifications-list">
                  {pending.length === 0 ? (
                    <div className="px-3 py-8 text-center text-sm text-[color:var(--cx-muted)]" data-testid="notifications-empty">
                      You’re all caught up.
                    </div>
                  ) : (
                    pending.map((m) => (
                      <div key={m.id} className="px-3 py-2.5 flex items-start gap-2.5 border-b last:border-b-0 border-[color:var(--cx-border)]"
                        data-testid="notification-pending-row" data-email={m.email}>
                        <span className="h-8 w-8 rounded-full bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center text-xs font-medium text-[color:var(--cx-primary)] shrink-0">
                          {(m.name || m.email).slice(0, 2).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-[color:var(--cx-fg)]">{m.name || m.email.split('@')[0]}</div>
                          <div className="truncate text-xs text-[color:var(--cx-muted)]">{m.email}</div>
                          <div className="text-[10px] text-[color:var(--cx-muted)] mt-0.5">Requested {timeAgo(m.created_at)}</div>
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <Button size="sm" className="h-7 px-2.5" disabled={busyId === m.id} onClick={() => approve(m.id)} data-testid="notification-approve-button">
                              <Check className="h-3.5 w-3.5 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[color:var(--destructive)]" disabled={busyId === m.id} onClick={() => reject(m.id)} data-testid="notification-reject-button">
                              <X className="h-3.5 w-3.5 mr-1" />Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-2 border-t border-[color:var(--cx-border)]">
                  <Button asChild variant="secondary" size="sm" className="w-full">
                    <Link to="/settings?tab=team" data-testid="notification-open-team"><Users className="h-4 w-4 mr-1.5" />Open Team &amp; access</Link>
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="topbar-user-menu"
                className="flex items-center gap-2 rounded-full border border-[color:var(--cx-border)] bg-[color:var(--cx-surface)] pl-1 pr-2 py-1 hover:border-[rgba(46,230,197,0.4)] transition-colors">
                <span className="h-7 w-7 rounded-full bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center text-xs font-medium text-[color:var(--cx-primary)]">
                  {initials}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-[color:var(--cx-muted)]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-[color:var(--cx-muted)]" />
                  <div className="min-w-0">
                    <div className="truncate text-sm">{user?.name || 'User'}</div>
                    <div className="truncate text-xs text-[color:var(--cx-muted)]">{user?.email}</div>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--cx-border)] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[color:var(--cx-fg-muted)]">
                    {canManage ? <Settings2 className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {canManage ? 'Manage' : 'View only'}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canManage && (
                <DropdownMenuItem asChild>
                  <Link to="/admin" data-testid="menu-admin-link"><Settings2 className="h-4 w-4 mr-2" />Manage apps</Link>
                </DropdownMenuItem>
              )}
              {canManage && (
                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=team" data-testid="menu-team-link">
                    <Users className="h-4 w-4 mr-2" />Team &amp; access
                    {pendingCount > 0 && (
                      <Badge className="ml-auto h-5 min-w-5 px-1.5 justify-center rounded-full text-[10px]"
                        style={{ background: 'var(--cx-primary)', color: '#04120f', border: 'none' }}>
                        {pendingCount}
                      </Badge>
                    )}
                  </Link>
                </DropdownMenuItem>
              )}
              {canManage && (
                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=branding" data-testid="menu-branding-link"><Palette className="h-4 w-4 mr-2" />Branding</Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to="/settings" data-testid="menu-settings-link"><KeyRound className="h-4 w-4 mr-2" />Account settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="topbar-logout-button" className="text-[color:var(--destructive)] focus:text-[color:var(--destructive)]">
                <LogOut className="h-4 w-4 mr-2" />Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
